/**
 * Page-level smooth scroll (Lenis), matched to the safe defaults used by
 * premium Shopify themes like Radian:
 *  - lerp 0.1, smoothWheel on desktop only.
 *  - Fully skipped on touch/coarse-pointer devices — mobile keeps native
 *    scrolling, never hijacked.
 *  - Fully skipped under prefers-reduced-motion.
 *  - Stops/starts in sync with Horizon's own scroll lock (the
 *    `scroll-lock` attribute Horizon's dialog/drawer components set on
 *    <html>), so it never fights the cart drawer, search modal, or mobile
 *    menu.
 *  - Off entirely unless the merchant enables it (theme setting), same as
 *    Horizon's other motion toggles.
 *
 * The Lenis vendor library itself is only fetched when actually needed —
 * skipped completely on mobile/reduced-motion so those visitors never pay
 * for it.
 */
(function () {
  'use strict';

  var INSTANCE_PROP = '__luxLenisController';

  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var coarsePointerQuery = window.matchMedia ? window.matchMedia('(hover: none) and (pointer: coarse)') : null;

  function prefersReducedMotion() {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function isTouchPrimary() {
    return !!(coarsePointerQuery && coarsePointerQuery.matches);
  }

  function isEnabled() {
    return !!(document.body && document.body.getAttribute('data-lenis-enabled') === 'true');
  }

  function canUseLenis() {
    return isEnabled() && !prefersReducedMotion() && !isTouchPrimary();
  }

  function isScrollLocked() {
    return document.documentElement.hasAttribute('scroll-lock');
  }

  var lockObserver = null;

  function syncScrollLock(controller) {
    if (!controller || !controller.lenis) return;
    if (isScrollLocked()) {
      controller.lenis.stop();
    } else {
      controller.lenis.start();
    }
  }

  function observeScrollLock(controller) {
    if (typeof MutationObserver !== 'function') return;
    if (lockObserver) lockObserver.disconnect();
    lockObserver = new MutationObserver(function () {
      syncScrollLock(controller);
    });
    lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });
    syncScrollLock(controller);
  }

  var vendorLoadPromise = null;

  function loadVendor() {
    if (typeof window.Lenis === 'function') return Promise.resolve();
    if (vendorLoadPromise) return vendorLoadPromise;

    var src = document.body && document.body.dataset.lenisVendorScript;
    if (!src) return Promise.resolve();

    vendorLoadPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return vendorLoadPromise;
  }

  function createLenis() {
    if (!canUseLenis() || typeof window.Lenis !== 'function') return null;
    if (window[INSTANCE_PROP]) return window[INSTANCE_PROP];

    var lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
    });

    var controller = { lenis: lenis, rafId: 0 };

    function raf(time) {
      lenis.raf(time);
      controller.rafId = window.requestAnimationFrame(raf);
    }
    controller.rafId = window.requestAnimationFrame(raf);

    window[INSTANCE_PROP] = controller;
    observeScrollLock(controller);

    return controller;
  }

  function destroyLenis() {
    var controller = window[INSTANCE_PROP];
    if (!controller) return;

    if (controller.rafId) window.cancelAnimationFrame(controller.rafId);
    if (lockObserver) {
      lockObserver.disconnect();
      lockObserver = null;
    }
    controller.lenis.destroy();
    window[INSTANCE_PROP] = null;
  }

  function sync() {
    if (canUseLenis()) {
      if (typeof window.Lenis === 'function') {
        createLenis();
      } else {
        loadVendor()
          .then(function () {
            if (canUseLenis()) createLenis();
          })
          .catch(function () {
            vendorLoadPromise = null;
          });
      }
    } else {
      destroyLenis();
    }
  }

  function init() {
    sync();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  if (reducedMotionQuery) reducedMotionQuery.addEventListener('change', sync);
  if (coarsePointerQuery) coarsePointerQuery.addEventListener('change', sync);

  // Layout can change height when a section is swapped in the theme
  // editor (or via the Section Rendering API) — let Lenis re-measure.
  document.addEventListener('shopify:section:load', function () {
    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      window.requestAnimationFrame(function () {
        controller.lenis.resize();
      });
    }
  });
})();
